import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, List, ListItemButton, ListItemText, ListItemIcon } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
// import { Description } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Navbar from '../shared/Navbar';
import { COLORS } from '../../shared/colors';
import { ICONS } from '../../shared/icons';
import { USER_ROLES, PROJECT_TYPES, ROUTES_PATHS } from '../../shared/appConstants';

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  const { filters, projectDropdownsByUser } = useSelector(state => state.tickets);
  
  const selectedProjectId = filters?.selectedProject;
  const selectedProject = projectDropdownsByUser?.find(p => String(p.id) === String(selectedProjectId));
  const lastSelectedView = localStorage.getItem('lastSelectedView');
  const isScrumProject = selectedProject?.project_type?.toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase() || (!selectedProject && lastSelectedView === ROUTES_PATHS.BACKLOG_MANAGEMENT);

  const isAISettingsEnabled = process.env.REACT_APP_ENABLE_AI_SETTINGS === 'true';
  const canAccessAdminSettings = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;

  // check user role for access for settings items (ADMIN or PM)
  const isAuthorized = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;

  // AI Developer Agent settings - commented for future implementation.
  // const handleAIAgentClick = () => {
  //   navigate('/ai-developer-settings');
  // };

  // AI Agent Model settings - commented for future implementation.
  // const handleAIAgentModelClick = () => {
  //   navigate('/ai-agent-model-settings');
  // };

  const handleProjectAlertsClick = () => {
    navigate('/project-alerts');
  };

  const handleNotificationSettingsClick = () => {
    navigate('/notification-settings');
  };

  const handleSprintSettingsClick = () => {
    navigate('/sprint-settings');
  };
  const handleStrategistSettingsClick = () => navigate('/strategist-model-settings');

  // const handlePromptTemplatesClick = () => {
  //   navigate('/prompt-templates');
  // };

  const settingsItems = [
    ...(canAccessAdminSettings && isAISettingsEnabled ? [
      // AI Developer Agent - commented for future implementation.
      // {
      //   title: t('settingsPage.aiDeveloperAgent.title'),
      //   description: t('settingsPage.aiDeveloperAgent.description'),
      //   iconSrc: ICONS.aiAgentDeveloper,
      //   onClick: handleAIAgentClick
      // },
      // AI Agent Model - commented for future implementation.
      // {
      //   title: t('settingsPage.aiAgentModel.title'),
      //   description: t('settingsPage.aiAgentModel.description'),
      //   iconSrc: ICONS.aiAgentModel,
      //   onClick: handleAIAgentModelClick
      // },
      {
        title: t('projectAlerts.title'),
        description: t('projectAlerts.description'),
        iconSrc: ICONS.projectAlert,
        onClick: handleProjectAlertsClick
      }
    ] : []),
    {
      title: t('settingsPage.notificationSettings.title'),
      description: t('settingsPage.notificationSettings.description'),
      iconSrc: ICONS.notificationSystem,
      onClick: handleNotificationSettingsClick
    },
    ...(isAuthorized && isScrumProject ? [{
      title: t('settingsPage.sprintSettings.title', 'Sprint Settings'),
      description: t('settingsPage.sprintSettings.description', 'Manage sprint configurations'),
      iconSrc: '/icons/settings.svg',
      onClick: handleSprintSettingsClick
    }] : []),
    ...(isAuthorized ? [{ title: t('settingsPage.strategistAgent.title'), description: t('settingsPage.strategistAgent.description'), iconSrc: ICONS.strategistConfig, onClick: handleStrategistSettingsClick }] : [])
  ];


  return (
    <div className="dashboard">
      <Navbar />
      
      <main className="main">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1,
            borderRadius: '8px'
          }}
        >
          <Typography variant="h6" fontWeight={600} color={COLORS.text.muted}>
            {t('settingsPage.title')}
          </Typography>
          <Typography
            component="span"
            sx={{
              border: 0,
              clip: 'rect(0 0 0 0)',
              height: 1,
              m: -1,
              overflow: 'hidden',
              p: 0,
              position: 'absolute',
              whiteSpace: 'nowrap',
              width: 1
            }}
          >
            {t('settingsPage.subtitle')}
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: '#F2F6FF',
            borderRadius: '8px',
            minHeight: 'calc(100vh - 190px)',
            p: { xs: 2, md: 4 }
          }}
        >
          <List sx={{ p: 0 }}>
            {settingsItems.map((item) => (
              <ListItemButton
                key={item.title}
                onClick={item.onClick}
                sx={{
                  bgcolor: COLORS.white,
                  border: `1px solid ${COLORS.brand.primaryDark}`,
                  borderRadius: '6px',
                  mb: 1.5,
                  minHeight: 54,
                  px: { xs: 1.5, md: 2 },
                  py: 0.75,
                  gap: 1.25,
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease, background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: COLORS.white,
                    boxShadow: '0 8px 20px rgba(8, 43, 114, 0.12)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1
                  }}
                >
                  {item.iconSrc ? (
                    <Box
                      component="img"
                      src={item.iconSrc}
                      alt=""
                      aria-hidden="true"
                      sx={{ width: 24, height: 24, objectFit: 'contain' }}
                    />
                  ) : item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  secondary={item.description}
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: COLORS.pureBlack
                  }}
                  secondaryTypographyProps={{
                    mt: 0.25,
                    fontSize: '0.8rem',
                    color: COLORS.ui.grey999
                  }}
                />
                <ChevronRight sx={{ color: COLORS.pureBlack, fontSize: 24 }} />
              </ListItemButton>
            ))}

            {/* Prompt Templates - Currently not in use, might need in future */}
              {/* <ListItem 
                button 
                onClick={handlePromptTemplatesClick}
                sx={{ 
                  '&:hover': { 
                    bgcolor: COLORS.gray[50],
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-1px)'
                  },
                  cursor: 'pointer',
                  borderRadius: 2,
                  mb: 2,
                  py: 1.5,
                  border: `1px solid ${COLORS.gray[200]}`,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.2s ease-in-out',
                  bgcolor: 'white'
                }}
              >
                <ListItemIcon>
                  <Description sx={{ color: COLORS.purple[500] }} />
                </ListItemIcon>
                <ListItemText
                  primary="Prompt Templates"
                  secondary="Manage AI prompt templates"
                />
                <ChevronRight sx={{ color: COLORS.gray[400] }} />
              </ListItem> */}
          </List>
        </Box>
      </main>
    </div>
  );
};

export default Settings;
